import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { categoryColor, categoryLabel } from './indicatorConfig';
import { formatPercent, formatScore, formatValue } from './formatters';
import { INDICATOR_SCORE_MAX, TOTAL_SCORE_MAX } from './scoreScale';

/**
 * Strongest / weakest among indicators that actually affect this entity's
 * total. Weight is part of the pick: a 15% indicator at score 1 drags more
 * than a 5% indicator at score 1, and a zero-weight metric never appears.
 */
export function metricHighlights(contributions) {
  const scored = (contributions || []).filter(
    (item) => !item.missing && item.weight > 0 && item.score != null
  );
  if (!scored.length) return null;

  const strongest = [...scored].sort((a, b) => {
    if (a.score !== b.score) return b.score - a.score;
    if (a.weighted !== b.weighted) return b.weighted - a.weighted;
    return a.label.localeCompare(b.label);
  })[0];

  const weakest = [...scored]
    .map((item) => ({
      item,
      drag: (INDICATOR_SCORE_MAX - item.score) * (item.effectiveWeight || item.weight),
    }))
    .sort((a, b) => {
      if (a.drag !== b.drag) return b.drag - a.drag;
      if (a.item.score !== b.item.score) return a.item.score - b.item.score;
      return a.item.label.localeCompare(b.item.label);
    })[0];

  if (!weakest || weakest.drag <= 0 || weakest.item.id === strongest.id) {
    return { strongest, weakest: null };
  }
  return { strongest, weakest: weakest.item };
}

// Wide enough for the uppercase header plus the sort arrow TableSortLabel adds,
// since tableLayout: 'fixed' means these widths are what the header gets.
const COLUMN_WIDTHS = { value: 104, score: 132, weight: 96 };

const NUMERIC_CELL_SX = {
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  verticalAlign: 'top',
};

/**
 * Splits an indicator into the three states the table draws differently.
 *
 * `weight` is what the slider assigned; `effectiveWeight` is what actually
 * reached the total, which is 0 for anything missing. A missing indicator
 * therefore still has a ceiling worth drawing when its weight is simply
 * forfeited, but not when renormalizing has already handed that weight out.
 */
function describeRow(item, renormalize) {
  if (item.weight === 0) return { state: 'unweighted', ceilingWeight: 0 };
  if (item.missing) {
    return { state: 'missing', ceilingWeight: renormalize ? 0 : item.weight };
  }
  return { state: 'scored', ceilingWeight: item.effectiveWeight };
}

const SORT_VALUES = {
  // Assigned weight, not effective: renormalizing rescales every scored row by
  // the same factor, so the order is identical, and missing rows keep the
  // weight they lost instead of collapsing to zero.
  weight: (entry) => entry.item.weight,
  contribution: (entry) => entry.item.weighted,
  score: (entry) => (entry.item.missing ? null : entry.item.score),
  value: (entry) => {
    if (entry.item.missing) return null;
    const numeric = Number(entry.item.value);
    return Number.isFinite(numeric) ? numeric : null;
  },
  indicator: (entry) => entry.item.label,
};

function sortRows(rows, sort) {
  const read = SORT_VALUES[sort.key];
  const direction = sort.direction === 'asc' ? 1 : -1;

  return [...rows].sort((a, b) => {
    const av = read(a);
    const bv = read(b);

    // Indicators with no value sort last in both directions, the way a
    // spreadsheet keeps blanks out of the way.
    if (av === null && bv === null) return a.item.label.localeCompare(b.item.label);
    if (av === null) return 1;
    if (bv === null) return -1;

    if (typeof av === 'string') return av.localeCompare(bv) * direction;
    if (av !== bv) return (av - bv) * direction;

    // Ties break by weight, so wasted weight surfaces above unweighted rows,
    // then by points, so equally weighted indicators lead with the biggest
    // contributor rather than whichever name sorts first.
    if (a.item.weight !== b.item.weight) return b.item.weight - a.item.weight;
    if (a.item.weighted !== b.item.weighted) return b.item.weighted - a.item.weighted;
    return a.item.label.localeCompare(b.item.label);
  });
}

const ContributionBar = ({ state, color, trackPercent, fillPercent }) => (
  <Box
    data-testid="contribution-track"
    // Widths are per-row data, so they go inline rather than minting a fresh
    // emotion class for every distinct percentage.
    style={{ width: `${trackPercent}%` }}
    sx={{
      height: 6,
      minWidth: trackPercent > 0 ? 2 : 0,
      borderRadius: 3,
      bgcolor: state === 'missing' ? 'transparent' : alpha(color, 0.16),
      border: state === 'missing' ? '1px dashed' : 'none',
      borderColor: 'text.disabled',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}
  >
    <Box
      data-testid="contribution-fill"
      style={{ width: `${fillPercent}%` }}
      sx={{ height: '100%', borderRadius: 3, bgcolor: color }}
    />
  </Box>
);

const ContributionRow = ({ item, state, ceilingWeight, maxCeiling, total, entityName, renormalize }) => {
  const muted = state !== 'scored';
  const trackPercent =
    maxCeiling > 0 ? ((INDICATOR_SCORE_MAX * ceilingWeight) / maxCeiling) * 100 : 0;
  const fillPercent = state === 'scored' ? (item.score / INDICATOR_SCORE_MAX) * 100 : 0;
  const color = categoryColor(item.category);
  const category = categoryLabel(item.category);

  let tooltip;
  if (state === 'missing') {
    tooltip = renormalize
      ? `${category} · no data in the source workbook. Its ${formatPercent(
          item.weight,
          0
        )} of weight was redistributed across the indicators that do have values.`
      : `${category} · no data in the source workbook, so ${entityName} forfeits ${formatPercent(
          item.weight,
          0
        )} of weight and up to ${formatScore(INDICATOR_SCORE_MAX * item.weight)} points.`;
  } else if (state === 'unweighted') {
    tooltip = `${category} · ${formatValue(item.value, item.format)} scores ${formatScore(
      item.score
    )} of ${formatScore(INDICATOR_SCORE_MAX)}, but its weight is 0% so it does not affect the total.`;
  } else {
    tooltip = `${category} · ${formatValue(item.value, item.format)} scores ${formatScore(
      item.score
    )} of ${formatScore(INDICATOR_SCORE_MAX)}. At ${formatPercent(
      item.effectiveWeight,
      0
    )} of weight that adds ${formatScore(item.weighted)} of ${entityName}'s ${formatScore(
      total
    )} total.`;
  }

  return (
    <Tooltip title={tooltip} placement="left" arrow>
      <TableRow
        data-testid="contribution-row"
        data-state={state}
        data-contribution={item.weighted.toFixed(4)}
        sx={{ '&:hover': { bgcolor: 'action.hover' }, cursor: 'default' }}
      >
        <TableCell sx={{ verticalAlign: 'top', pr: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ fontSize: '0.8rem', fontWeight: 400, lineHeight: 1.3, color: muted ? 'text.disabled' : 'text.primary' }}
          >
            {item.label}
          </Typography>
        </TableCell>

        <TableCell align="right" sx={{ ...NUMERIC_CELL_SX, width: COLUMN_WIDTHS.value }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.8rem',
              fontWeight: 400,
              lineHeight: 1.3,
              color: muted ? 'text.disabled' : 'text.primary',
            }}
          >
            {state === 'missing' ? 'no data' : formatValue(item.value, item.format)}
          </Typography>
        </TableCell>

        <TableCell align="right" sx={{ ...NUMERIC_CELL_SX, width: COLUMN_WIDTHS.score }}>
          <Typography
            variant="body2"
            sx={{ fontSize: '0.8rem', fontWeight: 400, lineHeight: 1.3, color: muted ? 'text.disabled' : 'text.primary' }}
          >
            {state === 'missing' ? '—' : formatScore(item.score)}
          </Typography>
          <Box sx={{ mt: 0.45 }}>
            <ContributionBar
              state={state}
              color={color}
              trackPercent={trackPercent}
              fillPercent={fillPercent}
            />
          </Box>
        </TableCell>

        <TableCell align="right" sx={{ ...NUMERIC_CELL_SX, width: COLUMN_WIDTHS.weight }}>
          <Typography
            variant="body2"
            sx={{ fontSize: '0.8rem', fontWeight: 400, lineHeight: 1.3, color: muted ? 'text.disabled' : 'text.primary' }}
          >
            {formatPercent(state === 'scored' ? item.effectiveWeight : item.weight, 0)}
          </Typography>
          {state === 'missing' && (
            <Typography
              variant="caption"
              sx={{ color: 'warning.dark', fontSize: '0.62rem', fontStyle: 'italic' }}
            >
              {renormalize ? 'reallocated' : 'forfeited'}
            </Typography>
          )}
        </TableCell>
      </TableRow>
    </Tooltip>
  );
};

const EntityScoreDetail = ({ row, level, renormalize, onClose }) => {
  const [sort, setSort] = useState({ key: 'contribution', direction: 'desc' });

  const contributions = row?.contributions;
  const rows = useMemo(() => {
    if (!contributions) return [];
    const described = contributions.map((item) => ({ item, ...describeRow(item, renormalize) }));
    return sortRows(described, sort);
  }, [contributions, renormalize, sort]);

  if (!row) return null;

  const { entity, total, rank, coverage } = row;
  const title = entity.name.replace(/ School District$/, '').replace(/ Planning Region$/, '');
  const maxCeiling = rows.reduce(
    (max, entry) => Math.max(max, INDICATOR_SCORE_MAX * entry.ceilingWeight),
    0
  );
  const totalPercent = Math.max(0, Math.min(100, (total / INDICATOR_SCORE_MAX) * 100));
  const highlights = metricHighlights(contributions);

  const handleSort = (key) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'indicator' ? 'asc' : 'desc' }
    );
  };

  const headerCell = (key, label, align, width) => (
    <TableCell
      align={align}
      sortDirection={sort.key === key ? sort.direction : false}
      sx={{
        width,
        px: 1,
        py: 0.75,
        fontSize: '0.66rem',
        fontWeight: 400,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'text.secondary',
        lineHeight: 1.3,
        bgcolor: 'grey.50',
        whiteSpace: 'nowrap',
      }}
    >
      <TableSortLabel
        active={sort.key === key}
        direction={sort.key === key ? sort.direction : 'asc'}
        onClick={() => handleSort(key)}
        sx={{ fontWeight: 400, '&.Mui-active': { fontWeight: 400 } }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Drawer anchor="right" open={Boolean(row)} onClose={onClose}>
      <Box
        sx={{ width: { xs: '100vw', sm: 620, md: 720 }, maxWidth: '100vw', p: 2.5 }}
        data-testid="score-detail"
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main' }}>
              Rank {rank} · Score {formatScore(total)}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {title}
            </Typography>
            {level === 'district' && entity.region && (
              <Typography variant="body2" color="text.secondary">
                {entity.region}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label="Close score detail">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          {entity.allianceDistrict && <Chip size="small" color="primary" label="Alliance District" />}
          {level === 'region' && (
            <Chip size="small" variant="outlined" label={`${entity.districtCount} districts`} />
          )}
          <Chip
            size="small"
            variant="outlined"
            label={`${coverage.scored}/${coverage.total} indicators scored`}
          />
        </Stack>

        {coverage.missingWeight > 0 && (
          <Alert severity={renormalize ? 'info' : 'warning'} sx={{ mb: 2 }}>
            {formatPercent(coverage.missingWeight, 0)} of weight is on indicators with no data.
            {renormalize
              ? ' Totals are renormalized over the indicators that do have values.'
              : ` Those indicators contribute 0, which depresses every total by up to ${formatScore(
                  coverage.missingWeight * INDICATOR_SCORE_MAX
                )} points — the same behavior as the spreadsheet.`}
          </Alert>
        )}

        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Total score
            </Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              <Box component="span" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                {formatScore(total)}
              </Box>
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {' '}
                of {TOTAL_SCORE_MAX.toFixed(1)}
              </Box>
            </Typography>
          </Stack>
          <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'grey.200', overflow: 'hidden' }}>
            <Box sx={{ width: `${totalPercent}%`, height: '100%', bgcolor: 'primary.main' }} />
          </Box>
          {highlights && (
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="score-highlights"
              sx={{ mt: 1.25, lineHeight: 1.45 }}
            >
              {title} performs strongest on {highlights.strongest.label} (score{' '}
              {formatScore(highlights.strongest.score)}).
              {highlights.weakest && (
                <>
                  {' '}
                  {highlights.weakest.label} is the weakest showing (score{' '}
                  {formatScore(highlights.weakest.score)}) and, at{' '}
                  {formatPercent(highlights.weakest.effectiveWeight || highlights.weakest.weight)},
                  is pulling the opportunity score down.
                </>
              )}
            </Typography>
          )}
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          How the total was built
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          The bar under Score is the most each indicator could add; the solid fill is what {title}{' '}
          earned. A dashed outline is weight lost to missing data. Sort any column to re-rank.
        </Typography>

        {/* The numeric columns are fixed, so a narrow viewport scrolls instead
            of crushing the indicator names down to a few characters. */}
        <Box sx={{ overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              tableLayout: 'fixed',
              minWidth: 480,
              '& td': { px: 1, py: 0.75, borderColor: 'divider' },
            }}
          >
            <TableHead>
              <TableRow>
                {headerCell('indicator', 'Indicator', 'left')}
                {headerCell('value', 'Value', 'right', COLUMN_WIDTHS.value)}
                {headerCell('score', 'Score', 'right', COLUMN_WIDTHS.score)}
                {headerCell('weight', 'Weight', 'right', COLUMN_WIDTHS.weight)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((entry) => (
                <ContributionRow
                  key={entry.item.id}
                  item={entry.item}
                  state={entry.state}
                  ceilingWeight={entry.ceilingWeight}
                  maxCeiling={maxCeiling}
                  total={total}
                  entityName={title}
                  renormalize={renormalize}
                />
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 400,
                    color: 'text.primary',
                    borderBottom: 'none',
                  }}
                >
                  Total
                </TableCell>
                <TableCell sx={{ borderBottom: 'none' }} />
                <TableCell
                  align="right"
                  sx={{
                    ...NUMERIC_CELL_SX,
                    fontSize: '0.8rem',
                    fontWeight: 400,
                    color: 'text.primary',
                    borderBottom: 'none',
                  }}
                >
                  {formatScore(total)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    ...NUMERIC_CELL_SX,
                    fontSize: '0.8rem',
                    fontWeight: 400,
                    color: 'text.primary',
                    borderBottom: 'none',
                  }}
                >
                  {formatPercent(
                    rows.reduce((sum, entry) => sum + entry.item.effectiveWeight, 0),
                    0
                  )}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
      </Box>
    </Drawer>
  );
};

export default EntityScoreDetail;
